// Lightweight markdown renderer for Knowledge Centre articles.
// Uses react-markdown + remark-gfm with a tailored component map that matches
// the article typography defined in Phase 6.2.
import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const components = {
  h1: ({ children }) => (
    // Articles set their own H1 outside the markdown body. Any H1 inside
    // the body would break the single-H1-per-page rule, so we downgrade.
    <h2 className="font-heading mt-12 mb-4 text-[26px] lg:text-[30px] font-semibold text-[var(--navy)] tracking-tight">
      {children}
    </h2>
  ),
  h2: ({ children }) => (
    <h2 className="font-heading mt-12 mb-4 text-[26px] lg:text-[30px] font-semibold text-[var(--navy)] tracking-tight">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="font-heading mt-8 mb-3 text-[20px] lg:text-[22px] font-semibold text-[var(--navy)] tracking-tight">
      {children}
    </h3>
  ),
  p: ({ children }) => (
    <p className="my-4 text-[16.5px] lg:text-[17px] leading-[1.75] text-[var(--charcoal)]">
      {children}
    </p>
  ),
  ul: ({ children }) => (
    <ul className="my-4 pl-6 list-disc space-y-2 text-[16.5px] lg:text-[17px] leading-[1.75] text-[var(--charcoal)] marker:text-[var(--pharma-blue)]">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="my-4 pl-6 list-decimal space-y-2 text-[16.5px] lg:text-[17px] leading-[1.75] text-[var(--charcoal)] marker:text-[var(--pharma-blue)] marker:font-semibold">
      {children}
    </ol>
  ),
  li: ({ children }) => <li className="pl-1">{children}</li>,
  strong: ({ children }) => <strong className="text-[var(--navy)] font-semibold">{children}</strong>,
  em: ({ children }) => <em className="text-[var(--charcoal)]">{children}</em>,
  blockquote: ({ children }) => (
    <blockquote className="my-6 border-l-4 border-[var(--pharma-blue)] pl-5 italic text-[var(--slate)]">
      {children}
    </blockquote>
  ),
  code: ({ inline, children }) =>
    inline ? (
      <code className="bg-[var(--bg-alt)] px-1.5 py-0.5 rounded-[3px] text-[14px] font-mono text-[var(--navy)]">
        {children}
      </code>
    ) : (
      <code className="block bg-[var(--bg-alt)] p-4 rounded-[4px] text-[14px] font-mono text-[var(--charcoal)] overflow-x-auto">
        {children}
      </code>
    ),
  pre: ({ children }) => (
    <pre className="my-6 bg-[var(--bg-alt)] p-4 rounded-[4px] overflow-x-auto text-[14px] font-mono text-[var(--charcoal)]">
      {children}
    </pre>
  ),
  a: ({ href, children }) => (
    <a
      href={href}
      className="text-[var(--pharma-blue)] underline underline-offset-4 hover:text-[var(--navy)]"
      target={href?.startsWith("http") ? "_blank" : undefined}
      rel={href?.startsWith("http") ? "noreferrer" : undefined}
    >
      {children}
    </a>
  ),
  hr: () => <hr className="my-10 border-[var(--border)]" />,
};

const Markdown = ({ children }) => (
  <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
    {children}
  </ReactMarkdown>
);

export default Markdown;
